import React, { useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import * as EASY_CONNECTION_TRIGGERS from 'constants/easyConnectionTriggers';
import EasyConnection from '../EasyConnection/EasyConnection';
import { removeCommerceTriggers } from 'util/commerceUtil';
import { easyConnectionInserted } from 'analytics/mlEventAnalytics';
import * as S from './MacroText.styles';

const propTypes = {
  editableMacroContent: PropTypes.string.isRequired,
  easyConnectionData: PropTypes.shape({
    connections: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    customerName: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    toDisplay: PropTypes.bool.isRequired,
  }).isRequired,
  onReplaceTrigger: PropTypes.func.isRequired,
};

const MacroText = ({ editableMacroContent, onReplaceTrigger, easyConnectionData, disableManualCopy }) => {
  const [activeEasyConnection, setActiveEasyConnection] = useState(null);

  /**
   * Replaces macro easy connection trigger with easy connection text provided
   * by our ML service
   * @param {object} params information needed to replace text with easy connection
   */
  const handleReplaceTrigger = (params) => {
    const { originalText, newText, options, usedEasyConnections = [] } = params;
    const updatedMacro = editableMacroContent.slice().replace(originalText, newText);

    // close popup, track selections for ML
    onReplaceTrigger({
      updatedMacro,
      usedEasyConnections: [...usedEasyConnections, params],
    });

    // analytics
    easyConnectionInserted({
      originalText,
      newText,
      options,
    });
  };

  /**
   * Takes an easy connection trigger and returns the options
   * provided by our ML service for that type of connection
   * @param {string} trigger text-based trigger for easy connections
   * @returns the easy connections options for the pased trigger
   */
  const getTriggerOptions = (trigger) => {
    if (!easyConnectionData || !easyConnectionData.toDisplay) {
      return false;
    }

    switch (trigger) {
      case EASY_CONNECTION_TRIGGERS.NAME.triggerText:
        return get(easyConnectionData, 'customerName');
      case EASY_CONNECTION_TRIGGERS.CONNECT_WITH_CUSTOMER.triggerText:
        return get(easyConnectionData, 'connections', []);
      default:
        return null;
    }
  };

  /**
   * Finds all active easy connection triggers in the current
   * macro and returns them in an array
   * @param {string} macroText text of the current macro
   * @returns {Array} all triggers present in the macro in the order they appear
   */
  const findValidTriggersInMacroText = (macroText) => {
    return Object.values(EASY_CONNECTION_TRIGGERS)
      .reduce((acc, trigger) => {
        const triggerText = trigger.triggerText;
        const options = getTriggerOptions(triggerText);

        if (macroText.includes(triggerText)) {
          const data = { ...trigger, options };

          return [...acc, data];
        }

        return acc;
      }, [])
      .sort((a, b) => {
        // logic needs the triggers to be ordered as they appear in macro text
        const aIdx = macroText.indexOf(a.triggerText);
        const bIdx = macroText.indexOf(b.triggerText);

        return aIdx - bIdx;
      });
  };

  const baseText = removeCommerceTriggers(editableMacroContent);

  return parse(baseText, {
    replace: (domNode) => {
      if (domNode && domNode.data) {
        const stringHTML = String(domNode.data);
        const easyConnectionTriggers = findValidTriggersInMacroText(stringHTML);

        if (!isEmpty(easyConnectionTriggers)) {
          const results = easyConnectionTriggers.map((trigger, idx) => {
            let lastTriggerEndIdx = 0;
            if (idx > 0) {
              // find the idx where the last trigger
              const previousTrigger = easyConnectionTriggers[idx - 1];
              const previousTriggerText = previousTrigger.triggerText;
              const previousTriggerLength = previousTriggerText.length;
              const previousTriggerStartIdx = stringHTML.indexOf(previousTriggerText);
              lastTriggerEndIdx = previousTriggerStartIdx + previousTriggerLength;
            }

            // data used by component
            const triggerText = trigger.triggerText;
            const highlightColor = trigger.color;
            const options = trigger.options || [];

            // get text between previous trigger and current trigger
            const triggerStartIdx = stringHTML.indexOf(triggerText);
            const firstText = stringHTML.slice(lastTriggerEndIdx, triggerStartIdx);

            return (
              <>
                {firstText}
                <EasyConnection
                  color={highlightColor}
                  triggerText={triggerText}
                  options={options}
                  onReplace={handleReplaceTrigger}
                  onSetActiveEasyConnection={(text) => setActiveEasyConnection(text)}
                  activeEasyConnection={activeEasyConnection}
                />
              </>
            );
          });

          // get text after last trigger
          const lastTrigger = easyConnectionTriggers[easyConnectionTriggers.length - 1];
          const lastTriggerStartIdx = stringHTML.indexOf(lastTrigger.triggerText);
          const lastTriggerLength = lastTrigger.triggerText.length;
          const lastTriggerEndIdx = lastTriggerStartIdx + lastTriggerLength;
          const lastText = stringHTML.slice(lastTriggerEndIdx);

          return (
            <S.MacroText disableManualCopy={disableManualCopy}>
              {results}
              {lastText}
            </S.MacroText>
          );
        }

        return <S.MacroText disableManualCopy={disableManualCopy}>{domNode?.data}</S.MacroText>;
      }
    },
  });
};

MacroText.propTypes = propTypes;

export default MacroText;
